import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Paper,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { toast } from 'react-toastify';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateCancelButtonSx,
  harmonateAbortButtonSx,
  harmonateDialogPaperProps,
  harmonateDialogTitleSx,
  harmonateDialogContentSx,
  harmonateDialogFormContentSx,
  harmonateFormFieldsSx,
  harmonateGridActionIconSx,
  harmonateSectionTitleSx,
  harmonateSwitchSx,
} from '../../../theme/harmonateStyles';
import { useFunctionPermissions } from '../../../hooks/useFunctionPermissions';
import { FC } from '../../../constants/functionCodes';

const getBaseUrl = () => process.env.REACT_APP_BACKEND_URL || '';
const CLIENTS_URL = `${getBaseUrl()}integrations/clients`;
const SCOPES_URL = `${getBaseUrl()}oauth2/scopes`;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const parseJsonSafely = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const parseScopeTokens = (scopeString) => (scopeString || '').split(/[\s,]+/).filter(Boolean);

const ApiClients = () => {
  const { canAdd, canEdit, canDelete } = useFunctionPermissions(FC.EXTERNAL_INTEGRATION);
  const [clients, setClients] = useState([]);
  const [scopeCatalog, setScopeCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [secretOpen, setSecretOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [createdSecret, setCreatedSecret] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '',
    redirectUri: 'https://dev.asrosync.com/callback',
    scopes: [],
  });

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [clientsRes, scopesRes] = await Promise.all([
        fetch(CLIENTS_URL, { headers: authHeaders() }),
        fetch(SCOPES_URL, { headers: { Accept: 'application/json' } }),
      ]);
      const clientsData = await parseJsonSafely(clientsRes);
      const scopesData = await parseJsonSafely(scopesRes);
      setClients(Array.isArray(clientsData) ? clientsData : []);
      setScopeCatalog(Array.isArray(scopesData) ? scopesData : []);
    } catch (e) {
      toast.error(e.message || 'Failed to load API clients');
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    if (!canAdd) return;
    setForm({
      name: '',
      redirectUri: 'https://dev.asrosync.com/callback',
      scopes: scopeCatalog.filter((s) => s.scope_name === 'conductor:read'),
    });
    setCreateOpen(true);
  };

  const openEdit = (row) => {
    if (!canEdit) return;
    setSelected(row);
    const tokens = new Set(parseScopeTokens(row.scopes || row.registered_scopes));
    setForm({
      name: row.name || '',
      redirectUri: row.redirect_uris || '',
      scopes: scopeCatalog.filter((s) => tokens.has(s.scope_name)),
    });
    setEditOpen(true);
  };

  const handleCreate = async () => {
    if (!canAdd) return;
    try {
      setSubmitting(true);
      const res = await fetch(CLIENTS_URL, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          name: form.name.trim(),
          redirectUri: form.redirectUri.trim(),
          scopes: form.scopes.map((s) => s.scope_name).join(' '),
          expiresAt: null,
        }),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Create failed (${res.status})`);
      }
      setCreateOpen(false);
      setCreatedSecret({
        clientId: data.clientId || data.client_id,
        clientSecret: data.clientSecret || data.client_secret,
        name: data.name,
      });
      setSecretOpen(true);
      await load();
      toast.success('API client created');
    } catch (e) {
      toast.error(e.message || 'Create failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async () => {
    if (!canEdit || !selected) return;
    try {
      setSubmitting(true);
      const clientId = selected.client_id || selected.clientId;
      const res = await fetch(`${CLIENTS_URL}/${encodeURIComponent(clientId)}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          scopes: form.scopes.map((s) => s.scope_name).join(' '),
        }),
      });
      const data = await parseJsonSafely(res);
      if (!res.ok) {
        throw new Error(data?.error || data?.message || `Update failed (${res.status})`);
      }
      setEditOpen(false);
      await load();
      toast.success('Scopes updated');
    } catch (e) {
      toast.error(e.message || 'Update failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleDisabled = async (row) => {
    if (!canEdit) return;
    try {
      const clientId = row.client_id || row.clientId;
      const disabled = !(row.disabled === true);
      const res = await fetch(`${CLIENTS_URL}/${encodeURIComponent(clientId)}/disable`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ disabled }),
      });
      if (!res.ok) {
        const data = await parseJsonSafely(res);
        throw new Error(data?.error || data?.message || `Update failed (${res.status})`);
      }
      await load();
      toast.success(disabled ? 'Client disabled' : 'Client enabled');
    } catch (e) {
      toast.error(e.message || 'Failed to update client');
    }
  };

  const openDelete = (row) => {
    if (!canDelete) return;
    setRowPendingDelete(row);
    setDeleteOpen(true);
  };

  const handleDelete = async () => {
    if (!canDelete || !rowPendingDelete) return;
    try {
      setDeleteLoading(true);
      const clientId = rowPendingDelete.client_id || rowPendingDelete.clientId;
      const res = await fetch(`${CLIENTS_URL}/${encodeURIComponent(clientId)}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await parseJsonSafely(res);
        throw new Error(data?.error || data?.message || `Delete failed (${res.status})`);
      }
      setDeleteOpen(false);
      setRowPendingDelete(null);
      await load();
      toast.success('Client deleted');
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setDeleteLoading(false);
    }
  };

  const copyText = async (value) => {
    const text = value == null ? '' : String(value);
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    try {
      if (navigator.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        toast.success('Copied');
        return;
      }
      // Fallback for HTTP / non-secure contexts (Clipboard API is blocked)
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      ta.style.top = '0';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      ta.setSelectionRange(0, text.length);
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      if (!ok) throw new Error('execCommand copy returned false');
      toast.success('Copied');
    } catch (e) {
      console.error('Copy failed:', e);
      toast.error('Copy failed — select and copy manually');
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1, minWidth: 140 },
      {
        field: 'client_id',
        headerName: 'Client ID',
        flex: 1.2,
        minWidth: 220,
        valueGetter: (params) => params.row.client_id || params.row.clientId,
      },
      {
        field: 'scopes',
        headerName: 'Scopes',
        flex: 1,
        minWidth: 160,
      },
      {
        field: 'disabled',
        headerName: 'Enabled',
        width: 110,
        sortable: false,
        renderCell: (params) => (
          <Switch
            size="small"
            checked={params.row.disabled !== true}
            onChange={() => handleToggleDisabled(params.row)}
            disabled={!canEdit}
            sx={harmonateSwitchSx}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        renderCell: (params) => (
          <Box>
            <Tooltip title={canEdit ? 'Edit scopes' : 'No edit permission'}>
              <span>
                <IconButton size="small" onClick={() => openEdit(params.row)} disabled={!canEdit} sx={harmonateGridActionIconSx}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={canDelete ? 'Delete' : 'No delete permission'}>
              <span>
                <IconButton size="small" onClick={() => openDelete(params.row)} disabled={!canDelete} sx={harmonateGridActionIconSx}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    [scopeCatalog, canEdit, canDelete]
  );

  const scopeField = (
    <Autocomplete
      multiple
      size="small"
      options={scopeCatalog}
      value={form.scopes}
      onChange={(_, value) => setForm((prev) => ({ ...prev, scopes: value }))}
      getOptionLabel={(option) =>
        option.display_name
          ? `${option.display_name} (${option.scope_name})`
          : option.scope_name
      }
      isOptionEqualToValue={(a, b) => a.scope_name === b.scope_name}
      renderTags={(tags, getTagProps) =>
        tags.map((option, index) => (
          <Chip
            label={option.display_name || option.scope_name}
            size="small"
            {...getTagProps({ index })}
            key={option.scope_name}
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 500,
              bgcolor: ht.secondaryTab,
              color: ht.text,
              borderRadius: ht.buttonRadius,
              '& .MuiChip-label': { px: 1.5 },
            }}
          />
        ))
      }
      renderInput={(params) => (
        <TextField {...params} label="Scopes" placeholder="Select scopes" />
      )}
    />
  );

  return (
    <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'transparent', boxShadow: 'none' }} elevation={0}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Box>
          <Typography variant="h6" sx={harmonateSectionTitleSx}>
            Conductor API Clients
          </Typography>
          <Typography variant="body2" sx={{ color: ht.text }}>
            Create credentials so partners (e.g. AsroSync) can call into Conductor.
          </Typography>
        </Box>
        <Tooltip title={canAdd ? 'Create client' : 'No add permission'}>
          <span>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} disabled={!canAdd} sx={harmonateProceedButtonSx}>
              Create client
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, minHeight: 360 }}>
        <DataGridPro
          rows={clients.map((c, idx) => ({
            id: c.client_id || c.clientId || String(idx),
            ...c,
          }))}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          rowHeight={40}
          pagination
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          sx={{
            ...harmonateDataGridSx,
            '& .MuiDataGrid-row:hover': { bgcolor: ht.secondaryTab, cursor: 'pointer' },
          }}
        />
      </Box>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth maxWidth="sm" PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Create API client</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField
            label="Name"
            size="small"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            fullWidth
            required
          />
          <TextField
            label="Redirect URI"
            size="small"
            value={form.redirectUri}
            onChange={(e) => setForm((p) => ({ ...p, redirectUri: e.target.value }))}
            fullWidth
            required
            helperText="Must match Sync callback URL"
          />
          {scopeField}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setCreateOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button
            variant="contained"
            disabled={submitting || !form.name.trim() || !form.redirectUri.trim()}
            onClick={handleCreate}
            sx={harmonateProceedButtonSx}
          >
            {submitting ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="sm" PaperProps={harmonateDialogPaperProps}>
        <DialogTitle sx={harmonateDialogTitleSx}>Edit scopes</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <TextField label="Name" size="small" value={form.name} fullWidth disabled />
          {scopeField}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={harmonateCancelButtonSx}>Cancel</Button>
          <Button variant="contained" disabled={submitting} onClick={handleEdit} sx={harmonateProceedButtonSx}>
            {submitting ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onClose={() => !deleteLoading && setDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Confirm Delete</DialogTitle>
        <DialogContent sx={harmonateDialogContentSx}>
          <Typography sx={{ color: ht.text }}>
            Are you sure you want to delete &quot;{rowPendingDelete?.name || 'selected'}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={deleteLoading} variant="outlined" sx={harmonateCancelButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            disabled={deleteLoading}
            sx={harmonateAbortButtonSx}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={secretOpen}
        onClose={() => {
          setSecretOpen(false);
          setCreatedSecret(null);
        }}
        fullWidth
        maxWidth="sm"
        PaperProps={harmonateDialogPaperProps}
      >
        <DialogTitle sx={harmonateDialogTitleSx}>Client created</DialogTitle>
        <DialogContent sx={{ ...harmonateDialogFormContentSx, ...harmonateFormFieldsSx, overflow: 'visible' }}>
          <Alert severity="warning" sx={{ borderColor: ht.blue, color: ht.text }}>
            Copy the client secret now. It will not be shown again.
          </Alert>
          <TextField
            label="Client ID"
            value={createdSecret?.clientId || ''}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyText(createdSecret?.clientId)} sx={{ color: ht.text }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
          <TextField
            label="Client Secret"
            value={createdSecret?.clientSecret || ''}
            fullWidth
            size="small"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton onClick={() => copyText(createdSecret?.clientSecret)} sx={{ color: ht.text }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            variant="contained"
            onClick={() => {
              setSecretOpen(false);
              setCreatedSecret(null);
            }}
            sx={harmonateProceedButtonSx}
          >
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ApiClients;
